import React, { useState, useEffect, useCallback } from 'react';
import { Lock, RefreshCw, Eye, Clock, AlertCircle, X, ChevronLeft, ChevronRight, CheckCircle, Filter, ChevronDown, TrendingUp, TrendingDown, Calendar } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import config from '@/config';
// ============= SAMPLE COMPONENTS =============

function BBoxAnnotator({ imageUrl, tags, review_lock_id, review_id, onComplete, containerMode }) {
  return (
    <div className="h-full flex flex-col items-center justify-center bg-gray-50 p-8">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-bold mb-4">BBox Annotator</h3>
        <img src={imageUrl} alt="Review" className="w-full rounded-lg mb-4" />
        <div className="space-y-2">
          <p className="text-sm text-gray-600">Review ID: {review_id}</p>
          <p className="text-sm text-gray-600">Lock ID: {review_lock_id}</p>
          <p className="text-sm text-gray-600">Tags: {tags?.map(t => t.tag).join(', ')}</p>
        </div>
        <div className="mt-6 flex gap-3">
          <button
            onClick={() => onComplete({ status: 'accepted', labels_data: [], comment: '' })}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Accept
          </button>
          <button
            onClick={() => onComplete({ status: 'rejected', labels_data: [], comment: 'Issues found' })}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}

function QualityCheckerComponent({ imageUrl, checklistItems, threshold, review_lock_id, review_id, onComplete }) {
  return (
    <div className="h-full flex flex-col items-center justify-center bg-gray-50 p-8">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-bold mb-4">Quality Checker</h3>
        <img src={imageUrl} alt="Review" className="w-full rounded-lg mb-4" />
        <div className="space-y-2">
          <p className="text-sm text-gray-600">Review ID: {review_id}</p>
          <p className="text-sm text-gray-600">Lock ID: {review_lock_id}</p>
          <p className="text-sm text-gray-600">Threshold: {threshold || 'N/A'}</p>
          <p className="text-sm text-gray-600">Checklist: {checklistItems?.length || 0} items</p>
        </div>
        <div className="mt-6 flex gap-3">
          <button
            onClick={() => onComplete({ status: 'accepted', labels_data: [], comment: '' })}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Pass
          </button>
          <button
            onClick={() => onComplete({ status: 'rejected', labels_data: [], comment: 'Quality issues' })}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Fail
          </button>
        </div>
      </div>
    </div>
  );
}

function BasicReviewComponent({ imageUrl, review_lock_id, review_id, onComplete }) {
  return (
    <div className="h-full flex flex-col items-center justify-center bg-gray-50 p-8">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-bold mb-4">Basic Review</h3>
        <img src={imageUrl} alt="Review" className="w-full rounded-lg mb-4" />
        <div className="space-y-2">
          <p className="text-sm text-gray-600">Review ID: {review_id}</p>
          <p className="text-sm text-gray-600">Lock ID: {review_lock_id}</p>
        </div>
        <div className="mt-6 flex gap-3">
          <button
            onClick={() => onComplete({ status: 'accepted', labels_data: [], comment: '' })}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Approve
          </button>
          <button
            onClick={() => onComplete({ status: 'rejected', labels_data: [], comment: 'Needs revision' })}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}

// ============= COMPONENT REGISTRY =============

const COMPONENT_REGISTRY = {
  'bbox_annotator': {
    component: BBoxAnnotator,
    propsMapping: (reviewData, lockId) => ({
      imageUrl: reviewData.image_url,
      tags: reviewData.component_props?.tags || [
        { tag: 'lens shape error', color: '#ef4444' },
        { tag: 'lens colour error', color: '#f59e0b' },
        { tag: 'background removal error', color: '#3b82f6' }
      ],
      review_lock_id: lockId,
      review_id: reviewData.id,
      containerMode: true,
      ...reviewData.component_props
    })
  },
  'quality_checker': {
    component: QualityCheckerComponent,
    propsMapping: (reviewData, lockId) => ({
      imageUrl: reviewData.image_url,
      checklistItems: reviewData.component_props?.checklist || [],
      threshold: reviewData.component_props?.threshold || 0.8,
      review_lock_id: lockId,
      review_id: reviewData.id,
      ...reviewData.component_props
    })
  },
  'basic_review': {
    component: BasicReviewComponent,
    propsMapping: (reviewData, lockId) => ({
      imageUrl: reviewData.image_url,
      review_lock_id: lockId,
      review_id: reviewData.id,
      ...reviewData.component_props
    })
  }
};

// ============= IMAGE OVERLAY COMPONENT =============

function ImageWithOverlay({ 
  imageUrl, 
  labels, 
  overlayAlpha = 0.3,
  borderWidth = 3,
  fontSize = 14,
  labelPadding = 5
}) {
  const canvasRef = React.useRef(null);
  const [imageLoaded, setImageLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!imageUrl || !canvasRef.current || !imageLoaded) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
    
    img.onload = () => {
      // Set canvas size to match image
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw the base image
      ctx.drawImage(img, 0, 0);
      
      // Draw segmentation masks first (so they appear below bboxes)
      if (labels?.segmentation && labels.segmentation.length > 0) {
        labels.segmentation.forEach((seg, index) => {
          if (seg.mask) {
            const maskImg = new Image();
            maskImg.crossOrigin = 'anonymous';
            maskImg.src = seg.mask;
            maskImg.onload = () => {
              ctx.globalAlpha = overlayAlpha;
              ctx.drawImage(maskImg, 0, 0, canvas.width, canvas.height);
              ctx.globalAlpha = 1.0;
            };
          }
        });
      }
      
      // Draw bounding boxes with colored fills
      const bboxes = [
        ...(labels?.object_recognition || []),
        ...(labels?.object_detection || [])
      ];
      
      console.log('Drawing bboxes:', bboxes);
      console.log('Canvas size:', canvas.width, 'x', canvas.height);
      
      if (bboxes.length > 0) {
        bboxes.forEach((bbox, index) => {
          console.log('Processing bbox:', bbox);
          
          if (bbox.bbox) {
            const { x, y, width, height } = bbox.bbox;
            
            console.log('BBox raw data:', { x, y, width, height });
            
            // ✅ ALWAYS treat as percentages since they're stored that way
            const xPx = (x / 100) * canvas.width;
            const yPx = (y / 100) * canvas.height;
            const wPx = (width / 100) * canvas.width;
            const hPx = (height / 100) * canvas.height;
            
            console.log('Converted to pixels:', { xPx, yPx, wPx, hPx });
            
            // Different colors for different boxes
            const colors = ['#ef4444', '#f59e0b', '#3b82f6', '#10b981', '#8b5cf6'];
            const color = colors[index % colors.length];
            
            // Draw semi-transparent filled rectangle
            ctx.fillStyle = color;
            ctx.globalAlpha = overlayAlpha;
            ctx.fillRect(xPx, yPx, wPx, hPx);
            ctx.globalAlpha = 1.0;
            
            // Draw border
            ctx.strokeStyle = color;
            ctx.lineWidth = borderWidth;
            ctx.strokeRect(xPx, yPx, wPx, hPx);
            
            // Draw label background and text
            if (bbox.tag) {
              ctx.font = `bold ${fontSize}px sans-serif`;
              const textWidth = ctx.measureText(bbox.tag).width;
              const labelHeight = fontSize + (labelPadding * 2);
              
              // Label background
              ctx.fillStyle = color;
              ctx.fillRect(xPx, yPx - labelHeight, textWidth + (labelPadding * 2), labelHeight);
              
              // Label text
              ctx.fillStyle = 'white';
              ctx.fillText(bbox.tag, xPx + labelPadding, yPx - labelPadding);
            }
          }
        });
      }
    };
  }, [imageUrl, labels, imageLoaded, overlayAlpha, borderWidth, fontSize, labelPadding]);

  return (
    <div className="relative w-full h-full">
      <img
        src={imageUrl}
        alt="Preview"
        className="hidden"
        onLoad={() => setImageLoaded(true)}
        crossOrigin="anonymous"
      />
      <canvas
        ref={canvasRef}
        className="w-full h-full object-contain"
      />
    </div>
  );
}

// ============= REVIEW KIND COLORS =============

const REVIEW_KIND_COLORS = {
  'final_human_review': {
    border: 'border-blue-400',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    badge: 'bg-blue-100 text-blue-800'
  },
  'quality_check': {
    border: 'border-green-400',
    bg: 'bg-green-50',
    text: 'text-green-700',
    badge: 'bg-green-100 text-green-800'
  },
  'initial_review': {
    border: 'border-purple-400',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    badge: 'bg-purple-100 text-purple-800'
  },
  'ai_validation': {
    border: 'border-orange-400',
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    badge: 'bg-orange-100 text-orange-800'
  },
  'default': {
    border: 'border-gray-400',
    bg: 'bg-gray-50',
    text: 'text-gray-700',
    badge: 'bg-gray-100 text-gray-800'
  }
};

// ============= DROPDOWN COMPONENT =============

function FilterDropdown({ label, children, isOpen, onToggle, hasActiveFilters }) {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
          hasActiveFilters 
            ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200' 
            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
        }`}
      >
        <span className="text-sm font-medium">{label}</span>
        {hasActiveFilters && (
          <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={onToggle}
          />
          <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[200px] z-20">
            {children}
          </div>
        </>
      )}
    </div>
  );
}

// ============= REVIEW MODAL =============

function ReviewModal({ isOpen, onClose, review, lockId, onComplete, allReviews, currentIndex, onNavigate }) {
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successAction, setSuccessAction] = useState('');
  const [isNavigating, setIsNavigating] = useState(false);
  const [componentKey, setComponentKey] = useState(0);

  useEffect(() => {
    setComponentKey(prev => prev + 1);
  }, [review?.id]);

  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen && !isCompleting && !isNavigating) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose, isCompleting, isNavigating]);

  if (!isOpen || !review) return null;

  const isPendingReview = review.status === 'pending';
  const isReadOnly = !isPendingReview;

  const componentId = review.review_component_id || 'basic_review';
  const componentConfig = COMPONENT_REGISTRY[componentId] || COMPONENT_REGISTRY['basic_review'];
  const ReviewComponent = componentConfig.component;
  const componentProps = componentConfig.propsMapping(review, lockId);

  const hasUnlockedPrevious = () => {
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (!allReviews[i].is_locked && allReviews[i].status === 'pending') return true;
    }
    return false;
  };

  const hasUnlockedNext = () => {
    for (let i = currentIndex + 1; i < allReviews.length; i++) {
      if (!allReviews[i].is_locked && allReviews[i].status === 'pending') return true;
    }
    return false;
  };

  const canGoPrevious = isPendingReview ? hasUnlockedPrevious() : currentIndex > 0;
  const canGoNext = isPendingReview ? hasUnlockedNext() : currentIndex < allReviews.length - 1;

  const handleNavigateClick = async (direction) => {
    setIsNavigating(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    onNavigate(direction);
    setIsNavigating(false);
  };

  const handleComplete = async (result) => {
    setIsCompleting(true);
    
    try {
      await onComplete(result);
      
      setSuccessAction(result.status);
      setShowSuccessMessage(true);
      await new Promise(resolve => setTimeout(resolve, 1500));
      setShowSuccessMessage(false);
      
      if (canGoNext) {
        setIsNavigating(true);
        await new Promise(resolve => setTimeout(resolve, 300));
        onNavigate('next');
        setIsNavigating(false);
      } else {
        onClose();
      }
      
      setIsCompleting(false);
    } catch (err) {
      console.error('Error completing review:', err);
      alert(`Failed to complete review: ${err.message}`);
      setIsCompleting(false);
    }
  };

  // Read-only viewer component for reviewed items
  const ReadOnlyViewer = () => {
    const labelCounts = {
      classification: review.labels?.classification?.length || 0,
      object_recognition: (review.labels?.object_recognition?.length || 0) + (review.labels?.object_detection?.length || 0),
      segmentation: review.labels?.segmentation?.length || 0
    };
    const totalLabels = labelCounts.classification + labelCounts.object_recognition + labelCounts.segmentation;

    return (
      <div className="h-full flex flex-col bg-gray-50">
        {/* Image section */}
        <div className="flex-1 flex items-center justify-center p-8 overflow-auto">
          <div className="max-w-5xl w-full">
            {review.image_url && totalLabels > 0 ? (
              <div className="bg-white rounded-lg shadow-lg p-4">
                <ImageWithOverlay 
                  imageUrl={review.image_url}
                  labels={review.labels}
                  overlayAlpha={0.3}
                  borderWidth={3}
                  fontSize={16}
                  labelPadding={6}
                />
              </div>
            ) : review.image_url ? (
              <img 
                src={review.image_url} 
                alt="Review" 
                className="w-full rounded-lg shadow-lg"
              />
            ) : (
              <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                <Eye className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No image available</p>
              </div>
            )}
          </div>
        </div>

        {/* Info panel */}
        <div className="bg-white border-t border-gray-200 p-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Status</p>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${
                  review.status === 'accepted'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {review.status === 'accepted' ? <CheckCircle className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  {review.status}
                </span>
              </div>
              
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Review Type</p>
                <p className="text-sm text-gray-900">{review.review_kind?.replace(/_/g, ' ')}</p>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Labels</p>
                <div className="flex flex-col gap-0.5">
                  {labelCounts.classification > 0 && (
                    <span className="text-xs text-gray-600">{labelCounts.classification} classification</span>
                  )}
                  {labelCounts.object_recognition > 0 && (
                    <span className="text-xs text-gray-600">{labelCounts.object_recognition} bounding box</span>
                  )}
                  {labelCounts.segmentation > 0 && (
                    <span className="text-xs text-gray-600">{labelCounts.segmentation} segmentation</span>
                  )}
                  {totalLabels === 0 && (
                    <span className="text-xs text-gray-400">No labels</span>
                  )}
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Reviewed By</p>
                <p className="text-sm text-gray-900">{review.completed_by || 'Unknown'}</p>
              </div>
            </div>

            {review.comment && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-500 mb-1">Comment</p>
                <p className="text-sm text-gray-900">{review.comment}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-[98vw] max-h-[98vh] flex flex-col overflow-hidden">
        <div className="bg-white border-b border-gray-200 p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {!isReadOnly && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleNavigateClick('previous')}
                    disabled={!canGoPrevious || isCompleting || isNavigating}
                    className="p-2 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-700" />
                  </button>
                  <button
                    onClick={() => handleNavigateClick('next')}
                    disabled={!canGoNext || isCompleting || isNavigating}
                    className="p-2 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-700" />
                  </button>
                </div>
              )}
              
              <div className="text-sm">
                <span className="font-medium text-gray-900">
                  {isReadOnly ? 'Reviewed Item' : `Review ${currentIndex + 1} of ${allReviews.length}`}
                </span>
                <span className="text-gray-400 mx-2">•</span>
                <span className="text-gray-600">{review.review_kind}</span>
                {isReadOnly && (
                  <>
                    <span className="text-gray-400 mx-2">•</span>
                    <span className={`font-medium ${review.status === 'accepted' ? 'text-green-600' : 'text-red-600'}`}>
                      {review.status}
                    </span>
                  </>
                )}
              </div>
            </div>

            <button
              onClick={onClose}
              disabled={isCompleting || isNavigating}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
              title="Close (ESC)"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-hidden">
          {isNavigating ? (
            <div className="h-full flex items-center justify-center bg-gray-50">
              <div className="flex flex-col items-center gap-4">
                <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin" />
                <p className="text-gray-600 font-medium">Loading next review...</p>
              </div>
            </div>
          ) : isReadOnly ? (
            <ReadOnlyViewer />
          ) : (
            <ReviewComponent 
              key={componentKey}
              {...componentProps}
              onComplete={handleComplete}
            />
          )}
        </div>
      </div>

      {showSuccessMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl p-8 flex flex-col items-center">
            <CheckCircle className={`w-16 h-16 mb-4 ${successAction === 'accepted' ? 'text-green-600' : 'text-red-600'}`} />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Review {successAction === 'accepted' ? 'Accepted' : 'Rejected'}
            </h3>
            <p className="text-gray-600">
              {canGoNext ? 'Loading next review...' : 'All reviews completed!'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ============= MAIN ADMIN COMPONENT =============

function AdminReviewManager() {
  const { authenticatedFetch } = useAuth();
  
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [statusFilter, setStatusFilter] = useState('all');
  const [reviewKindFilter, setReviewKindFilter] = useState([]);
  const [sortOrder, setSortOrder] = useState('newest');
  
  const [availableReviewKinds, setAvailableReviewKinds] = useState([]);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  
  const [lockingReview, setLockingReview] = useState(null);
  const [currentLockId, setCurrentLockId] = useState(null);
  
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReviewIndex, setSelectedReviewIndex] = useState(null);

  // Dropdown states
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

  // Stats calculation
  const stats = {
    total: totalCount,
    pending: reviews.filter(r => r.status === 'pending').length,
    accepted: reviews.filter(r => r.status === 'accepted').length,
    rejected: reviews.filter(r => r.status === 'rejected').length,
  };

  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (reviewKindFilter.length > 0) params.set('kinds', reviewKindFilter.join(','));
    if (sortOrder !== 'newest') params.set('sort', sortOrder);
    if (currentPage !== 1) params.set('page', currentPage);
    
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [statusFilter, reviewKindFilter, sortOrder, currentPage]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    const kinds = params.get('kinds');
    const sort = params.get('sort');
    const page = params.get('page');
    
    if (status) setStatusFilter(status);
    if (kinds) setReviewKindFilter(kinds.split(','));
    if (sort) setSortOrder(sort);
    if (page) setCurrentPage(parseInt(page));
  }, []);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: currentPage,
        size: pageSize,
        sort: sortOrder
      });

      if (statusFilter === 'pending') {
        params.append('status', 'pending');
      } else if (statusFilter === 'reviewed') {
        params.append('status', 'accepted');
        params.append('status', 'rejected');
        params.append('status', 'completed');
      }

      if (reviewKindFilter.length > 0) {
        reviewKindFilter.forEach(kind => {
          params.append('review_kind', kind);
        });
      }

      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/reviews/list/admin?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setReviews(data.data);
      setTotalPages(data.pagination.total_pages);
      setTotalCount(data.pagination.total_count);
      
      const kinds = [...new Set(data.data.map(r => r.review_kind))];
      setAvailableReviewKinds(kinds);
      
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, currentPage, pageSize, statusFilter, reviewKindFilter, sortOrder]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Handle click on pending (non-reviewed) review - Opens modal with lock
  const handlePendingReviewClick = async (reviewIndex) => {
    const review = reviews[reviewIndex];
    if (review.is_locked) return;
    
    setLockingReview(review.id);
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/reviews/lock/create`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ review_id: review.id })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create lock');
      }

      const data = await response.json();
      setCurrentLockId(data.lock.id);
      setSelectedReviewIndex(reviewIndex);
      setModalOpen(true);
      await fetchReviews();
      
    } catch (err) {
      console.error('Error creating lock:', err);
      alert(`Failed to lock review: ${err.message}`);
    } finally {
      setLockingReview(null);
    }
  };

  // Handle click on already reviewed review - Shows read-only view
  const handleReviewedReviewClick = (reviewIndex) => {
    const review = reviews[reviewIndex];
    setSelectedReviewIndex(reviewIndex);
    setModalOpen(true);
    // Note: No lock needed for reviewed items, so currentLockId stays null
  };

  const releaseLock = async (lockId) => {
    if (!lockId) return;
    
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/reviews/lock/release`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lock_id: lockId })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to release lock');
      }
      
    } catch (err) {
      console.error('Error releasing lock:', err);
    }
  };

  const handleCompleteReview = async (result) => {
    const payload = {
      lock_id: currentLockId,
      status: result.status,
      labels_data: result.labels_data
    };

    if (result.comment && result.comment.trim()) {
      payload.comment = result.comment.trim();
    }

    const response = await authenticatedFetch(
      `${config.BACKEND_API_ADDRESS}/reviews/lock/complete`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to complete review');
    }

    await fetchReviews();
  };

  const handleModalClose = async () => {
    setModalOpen(false);
    if (currentLockId) {
      await releaseLock(currentLockId);
      setCurrentLockId(null);
    }
    setSelectedReviewIndex(null);
    await fetchReviews();
  };

  const handleNavigate = async (direction) => {
    if (currentLockId) {
      await releaseLock(currentLockId);
      setCurrentLockId(null);
    }

    let newIndex = direction === 'next' ? selectedReviewIndex + 1 : selectedReviewIndex - 1;

    while (newIndex >= 0 && newIndex < reviews.length) {
      if (!reviews[newIndex].is_locked && reviews[newIndex].status === 'pending') {
        break;
      }
      newIndex = direction === 'next' ? newIndex + 1 : newIndex - 1;
    }

    if (newIndex < 0 || newIndex >= reviews.length) {
      alert('No more unlocked reviews available');
      setModalOpen(false);
      setSelectedReviewIndex(null);
      await fetchReviews();
      return;
    }

    const newReview = reviews[newIndex];
    
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/reviews/lock/create`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ review_id: newReview.id })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create lock');
      }

      const data = await response.json();
      setCurrentLockId(data.lock.id);
      setSelectedReviewIndex(newIndex);
      await fetchReviews();
      
    } catch (err) {
      console.error('Error locking new review:', err);
      alert(`Failed to lock review: ${err.message}`);
      setModalOpen(false);
      setSelectedReviewIndex(null);
      await fetchReviews();
    }
  };

  const toggleReviewKind = (kind) => {
    setReviewKindFilter(prev => 
      prev.includes(kind) 
        ? prev.filter(k => k !== kind)
        : [...prev, kind]
    );
    setCurrentPage(1);
  };

  const getReviewColors = (review) => {
    if (review.status !== 'pending') {
      return {
        border: 'border-gray-300',
        bg: 'bg-gray-100',
        text: 'text-gray-500',
        badge: 'bg-gray-200 text-gray-600'
      };
    }
    return REVIEW_KIND_COLORS[review.review_kind] || REVIEW_KIND_COLORS.default;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const clearAllFilters = () => {
    setStatusFilter('all');
    setReviewKindFilter([]);
    setSortOrder('newest');
    setCurrentPage(1);
  };

  const hasActiveFilters = statusFilter !== 'all' || reviewKindFilter.length > 0 || sortOrder !== 'newest';

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Top Navbar */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          {/* Title and Actions Row */}
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Review Manager</h1>
            <button
              onClick={fetchReviews}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="text-sm font-medium">Refresh</span>
            </button>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-blue-600 mb-1">Total Reviews</p>
                  <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-blue-400" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-yellow-600 mb-1">Pending</p>
                  <p className="text-2xl font-bold text-yellow-900">{stats.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-400" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-green-600 mb-1">Accepted</p>
                  <p className="text-2xl font-bold text-green-900">{stats.accepted}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-red-600 mb-1">Rejected</p>
                  <p className="text-2xl font-bold text-red-900">{stats.rejected}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Filter className="w-4 h-4" />
              <span className="font-medium">Filters:</span>
            </div>

            {/* Status Filter */}
            <FilterDropdown
              label={`Status: ${statusFilter === 'all' ? 'All' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}`}
              isOpen={statusDropdownOpen}
              onToggle={() => setStatusDropdownOpen(!statusDropdownOpen)}
              hasActiveFilters={statusFilter !== 'all'}
            >
              {['all', 'pending', 'reviewed'].map(status => (
                <button
                  key={status}
                  onClick={() => {
                    setStatusFilter(status);
                    setStatusDropdownOpen(false);
                    setCurrentPage(1);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                    statusFilter === status ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </FilterDropdown>

            {/* Review Type Filter */}
            <FilterDropdown
              label={`Type${reviewKindFilter.length > 0 ? ` (${reviewKindFilter.length})` : ': All'}`}
              isOpen={typeDropdownOpen}
              onToggle={() => setTypeDropdownOpen(!typeDropdownOpen)}
              hasActiveFilters={reviewKindFilter.length > 0}
            >
              <div className="max-h-64 overflow-y-auto">
                {availableReviewKinds.map(kind => (
                  <button
                    key={kind}
                    onClick={() => toggleReviewKind(kind)}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${
                      reviewKindFilter.includes(kind) ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                    }`}
                  >
                    <span>{kind.replace(/_/g, ' ')}</span>
                    {reviewKindFilter.includes(kind) && (
                      <CheckCircle className="w-4 h-4" />
                    )}
                  </button>
                ))}
              </div>
            </FilterDropdown>

            {/* Sort Filter */}
            <FilterDropdown
              label={`Sort: ${sortOrder === 'newest' ? 'Newest First' : 'Oldest First'}`}
              isOpen={sortDropdownOpen}
              onToggle={() => setSortDropdownOpen(!sortDropdownOpen)}
              hasActiveFilters={sortOrder !== 'newest'}
            >
              {['newest', 'oldest'].map(order => (
                <button
                  key={order}
                  onClick={() => {
                    setSortOrder(order);
                    setSortDropdownOpen(false);
                    setCurrentPage(1);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                    sortOrder === order ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  {order === 'newest' ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
                  {order === 'newest' ? 'Newest First' : 'Oldest First'}
                </button>
              ))}
            </FilterDropdown>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium px-3 py-2 rounded-lg hover:bg-indigo-50 transition-colors"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="h-full px-6 py-6">
          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Error loading reviews</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Debug Info */}
          {reviews.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <details className="text-xs">
                <summary className="cursor-pointer text-blue-700 font-medium hover:text-blue-800">
                  🔍 Debug: First Review Data (Click to expand)
                </summary>
                <pre className="mt-2 text-blue-900 overflow-auto max-h-96 bg-white p-3 rounded border border-blue-200">
                  {JSON.stringify(reviews[0], null, 2)}
                </pre>
              </details>
            </div>
          )}

          {/* Loading State */}
          {loading && reviews.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-4">
                <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin" />
                <p className="text-gray-600 font-medium">Loading reviews...</p>
              </div>
            </div>
          ) : reviews.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <Eye className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">No reviews found</h3>
                <p className="text-gray-600">Try adjusting your filters or check back later</p>
              </div>
            </div>
          ) : (
            /* Table View */
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preview</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Annotated</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Labels</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {reviews.map((review, index) => {
                      const colors = getReviewColors(review);
                      const isPending = review.status === 'pending';
                      const isLocked = review.is_locked;
                      const isClickable = isPending ? !isLocked : true;
                      
                      // Count labels
                      const labelCounts = {
                        classification: review.labels?.classification?.length || 0,
                        object_recognition: (review.labels?.object_recognition?.length || 0) + (review.labels?.object_detection?.length || 0),
                        segmentation: review.labels?.segmentation?.length || 0
                      };
                      const totalLabels = labelCounts.classification + labelCounts.object_recognition + labelCounts.segmentation;
                      const hasLabels = totalLabels > 0;
                      
                      return (
                        <tr 
                          key={review.id} 
                          className={`${isClickable ? 'hover:bg-gray-50 cursor-pointer' : 'bg-gray-50'} transition-colors`}
                          onClick={() => {
                            if (!isClickable) return;
                            if (isPending) {
                              handlePendingReviewClick(index);
                            } else {
                              handleReviewedReviewClick(index);
                            }
                          }}
                        >
                          <td className="px-6 py-4">
                            {review.image_url ? (
                              <img
                                src={review.image_url}
                                alt="Review"
                                className={`w-24 h-24 object-contain rounded-lg ${isLocked && isPending ? 'opacity-50' : ''}`}
                              />
                            ) : (
                              <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center">
                                <Eye className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {review.image_url && hasLabels ? (
                              <div className="w-24 h-24 rounded-lg overflow-hidden border border-gray-300">
                               <ImageWithOverlay 
                                imageUrl={review.image_url}
                                labels={review.labels}
                                overlayAlpha={0.5}
                                borderWidth={50}
                                fontSize={16}
                                labelPadding={8}
                              />
                              </div>
                            ) : (
                              <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                                <span className="text-xs text-gray-400">No labels</span>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 font-mono">{review.id}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.badge}`}>
                              {review.review_kind.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              review.status === 'pending' 
                                ? 'bg-yellow-100 text-yellow-800' 
                                : review.status === 'accepted'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {review.status === 'pending' && <Clock className="w-3 h-3" />}
                              {review.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-1">
                              {labelCounts.classification > 0 && (
                                <span className="text-xs text-gray-600">
                                  {labelCounts.classification} class
                                </span>
                              )}
                              {labelCounts.object_recognition > 0 && (
                                <span className="text-xs text-gray-600">
                                  {labelCounts.object_recognition} bbox
                                </span>
                              )}
                              {labelCounts.segmentation > 0 && (
                                <span className="text-xs text-gray-600">
                                  {labelCounts.segmentation} mask
                                </span>
                              )}
                              {!hasLabels && (
                                <span className="text-xs text-gray-400">None</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">{formatDate(review.created_at)}</td>
                          <td className="px-6 py-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isPending) {
                                  handlePendingReviewClick(index);
                                } else {
                                  handleReviewedReviewClick(index);
                                }
                              }}
                              disabled={isPending && (isLocked || lockingReview === review.id)}
                              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-xs font-medium ${
                                isPending && isLocked
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : isPending
                                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                  : 'bg-gray-600 text-white hover:bg-gray-700'
                              }`}
                            >
                              {lockingReview === review.id ? (
                                <>
                                  <RefreshCw className="w-3 h-3 animate-spin" />
                                  Locking...
                                </>
                              ) : isPending && isLocked ? (
                                <>
                                  <Lock className="w-3 h-3" />
                                  Locked
                                </>
                              ) : isPending ? (
                                'Review'
                              ) : (
                                <>
                                  <Eye className="w-3 h-3" />
                                  View
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4">
              <div className="text-sm text-gray-600">
                Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * pageSize, totalCount)}</span> of{' '}
                <span className="font-medium">{totalCount}</span> reviews
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg ${
                          currentPage === pageNum
                            ? 'bg-indigo-600 text-white'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Review Modal */}
      {modalOpen && selectedReviewIndex !== null && reviews[selectedReviewIndex] && (
        <ReviewModal
          isOpen={modalOpen}
          onClose={handleModalClose}
          review={reviews[selectedReviewIndex]}
          lockId={currentLockId}
          onComplete={handleCompleteReview}
          allReviews={reviews}
          currentIndex={selectedReviewIndex}
          onNavigate={handleNavigate}
        />
      )}
    </div>
  );
}

export default AdminReviewManager;